import { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Loader2, BellRing, Ticket } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function WaitlistStatusPage() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [prevPos, setPrevPos] = useState(null);
  const [notFound, setNotFound] = useState(false);

  const load = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/waitlist/status/${token}`);
      setData(d => { if (d?.position && r.data.position && r.data.position < d.position) setPrevPos(d.position); return r.data; });
    } catch { setNotFound(true); }
  }, [token]);
  useEffect(() => { load(); const i = setInterval(load, 20000); return () => clearInterval(i); }, [load]);

  const leave = async () => {
    if (!window.confirm('Leave the waitlist? You lose your place in line.')) return;
    await axios.delete(`${API}/waitlist/status/${token}`);
    toast.success('You left the waitlist');
    load();
  };

  if (notFound) return <div className="min-h-screen flex items-center justify-center text-muted-foreground">Waitlist entry not found.</div>;
  if (!data) return <div className="min-h-screen flex items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>;

  const ev = data.event || {};
  return (
    <div className="min-h-screen bg-background pt-24 pb-10 px-4">
      <div className="max-w-md mx-auto space-y-4" data-testid="waitlist-status-page">
        {ev.image_url && <img src={ev.image_url} alt="" className="w-full h-36 object-cover rounded-xl" />}
        <div className="text-center">
          <h1 className="text-xl font-bold">{ev.name || ev.title}</h1>
          <p className="text-sm text-muted-foreground">{ev.event_date}{ev.venue_name ? ` · ${ev.venue_name}` : ''}</p>
        </div>
        {data.organizer_message && (
          <p className="text-xs text-center text-amber-300 bg-amber-500/10 border border-amber-500/30 rounded-md px-3 py-2" data-testid="waitlist-status-org-message">
            💬 From the organizer: {data.organizer_message}
          </p>
        )}
        {data.status === 'waiting' ? (
          <Card className="border-amber-500/40"><CardContent className="py-8 text-center space-y-2">
            <BellRing className="h-8 w-8 mx-auto text-amber-400" />
            <p className="text-4xl font-extrabold text-amber-300" data-testid="waitlist-position">#{data.position}</p>
            <p className="text-sm text-muted-foreground">of {data.total_waiting} in line · {data.qty} ticket{data.qty > 1 ? 's' : ''} wanted</p>
            {prevPos && <p className="text-xs text-emerald-400 font-semibold">▲ You moved up from #{prevPos}</p>}
            <p className="text-xs text-muted-foreground">
              {data.card_saved
                ? '✓ Card on file — when your turn comes you\'re charged automatically and your QR ticket is emailed instantly.'
                : '⚠ No card on file — people with a saved card are served first. Rejoin from the event page to add one.'}
            </p>
            <Button variant="outline" size="sm" className="text-rose-400" onClick={leave} data-testid="waitlist-leave-btn">Leave the waitlist</Button>
          </CardContent></Card>
        ) : data.status === 'charged' ? (
          <Card className="border-emerald-500/40"><CardContent className="py-8 text-center space-y-2">
            <Ticket className="h-8 w-8 mx-auto text-emerald-400" />
            <p className="text-xl font-bold text-emerald-400">You got tickets! 🎟️</p>
            <p className="text-sm text-muted-foreground">Your card was charged and your QR ticket{data.qty > 1 ? 's were' : ' was'} emailed to you.</p>
          </CardContent></Card>
        ) : data.status === 'payment_failed' ? (
          <Card className="border-rose-500/40"><CardContent className="py-8 text-center space-y-2">
            <p className="text-lg font-bold text-rose-400">Your card couldn't be charged</p>
            <p className="text-sm text-muted-foreground">A ticket opened up but the payment failed — check your email for a direct purchase link.</p>
          </CardContent></Card>
        ) : data.status === 'closed' ? (
          <Card><CardContent className="py-8 text-center space-y-2">
            <p className="text-lg font-bold">The waitlist has closed</p>
            <p className="text-sm text-muted-foreground">The event has wrapped up. Your card was never charged and we've removed it — check your email for links to follow the organizer so you catch the next one.</p>
          </CardContent></Card>
        ) : (
          <Card><CardContent className="py-8 text-center text-sm text-muted-foreground">You're no longer on this waitlist.</CardContent></Card>
        )}
        <p className="text-[10px] text-center text-muted-foreground">This page updates automatically.</p>
      </div>
    </div>
  );
}
